import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Auth } from "aws-amplify";
import { APP_REGIONS, USER_ROLES } from "../constants";
import { featureFlags, EnvironmentNames } from "../featureFlags";

export interface AuthContextType {
  assignedRoles: string[] | null;
  assignedCountries: string[] | null;
  isLoading: boolean;
  error: string | null;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [assignedRoles, setAssignedRoles] = useState<string[] | null>(null);
  const [assignedCountries, setAssignedCountries] = useState<string[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const extractUserGroups = async (): Promise<{
    roles: string[];
    countries: string[];
  }> => {
    const user = await Auth.currentAuthenticatedUser();
    const idToken = user.signInUserSession?.idToken;
    const groups = idToken?.payload?.["cognito:groups"] || [];

    const roles: string[] = [];
    const countries: string[] = [];

    // AE is a country entitlement for UAE-only pages. It is not an APP_REGIONS
    // entry, so the header region control does not offer it.
    const countryGroups = new Set([...Object.keys(APP_REGIONS), "AE"]);

    groups.forEach((group: string) => {
      if (group.startsWith("role:")) {
        const roleCode = group.split(":")[1];
        roles.push(roleCode);
      } else if (group.startsWith("country:")) {
        const countryCode = group.split(":")[1];
        if (countryGroups.has(countryCode)) {
          countries.push(countryCode);
        }
      }
    });

    return { roles, countries };
  };

  const fetchUserGroups = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      // If admin authorization is disabled, simulate super admin role
      const stage = process.env.REACT_APP_STAGE as EnvironmentNames;
      if (!featureFlags[stage]?.enableAdminAuthorization) {
        setAssignedRoles([USER_ROLES.SUPER_ADMIN]);
        setAssignedCountries(Object.keys(APP_REGIONS));
        setIsLoading(false);
        return;
      }

      const groups = await extractUserGroups();
      setAssignedRoles(groups.roles);
      setAssignedCountries(groups.countries);
    } catch (err) {
      console.error("Error fetching user groups:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch user groups"
      );
      setAssignedRoles(null);
      setAssignedCountries(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshAuth = async () => {
    await fetchUserGroups();
  };

  useEffect(() => {
    fetchUserGroups();
  }, [fetchUserGroups]);

  const contextValue: AuthContextType = {
    assignedRoles,
    assignedCountries,
    isLoading,
    error,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
